import React, { useEffect, useReducer, useRef, useState } from "react";

import { ComboBoxChangeEvent } from "@progress/kendo-react-dropdowns";
import { Input, SwitchChangeEvent } from "@progress/kendo-react-inputs";
import { useTranslation } from "react-i18next";
import { useSelector } from "react-redux";

import Endpoint from "../../../../services/api/endpoint";
import { getInitialState } from "../../../../state";
import { supplierReducer } from "../../../../state/reducers";
import { IApplicationState } from "../../../../store";
import { handleChange } from "../../../../utils/editorUtils";
import { IAddress, ISupplier, IUser } from "../../../../utils/types/models";
import { isNullOrEmpty } from "../../../../utils/utils";
import EntityEditor, { AddressEdit, IEditorProps, IEditScreenProps, YesNoSwitch } from "../../../global/editor";

const SupplierEditor: React.FC<IEditorProps<ISupplier>> = (props: IEditorProps<ISupplier>) => {
	const { t } = useTranslation();
	const [state, dispatch] = useReducer(supplierReducer, getInitialState<ISupplier>());
	const currentUser: IUser = useSelector((reduxState: IApplicationState) => reduxState.authenticationState.currentUser);
	const initalSupplier: ISupplier = {
		id: (props as IEditScreenProps<ISupplier>).recordId || 0,
		name: "",
		address: {
			street: "",
			number: "",
			postalCode: "",
			municipality: "",
			province: "",
			country: "",
			fullAddress: ""
		},
		active: true,
		companyId: currentUser.currentCompanyId,
		ourCustomerNumber: ""
	};
	const [supplier, setSupplier] = useState<ISupplier>(initalSupplier);
	const [dataChange, setDataChanged] = useState<boolean>(false);
	const firstField: React.MutableRefObject<Input> = useRef();

	useEffect(() => {
		if (state.entity) {
			setSupplier(state.entity);
		}
	}, [state.entity]);

	function onChange(event: React.ChangeEvent<HTMLInputElement> | SwitchChangeEvent | ComboBoxChangeEvent): void {
		setSupplier(handleChange(supplier, event));
		setDataChanged(true);
	}

	function setAddress(address: IAddress): void {
		const newSupplier: ISupplier = { ...supplier };
		newSupplier.address = address;
		setSupplier(newSupplier);
		setDataChanged(true);
	}

	function getErrorMessages(): string[] {
		const messages: string[] = [];
		if (isNullOrEmpty(supplier.name)) {
			messages.push(t("fill_in_required_field", { field: t("name").toLowerCase() }));
		}
		if (isNullOrEmpty(supplier.address.street)) {
			messages.push(t("fill_in_required_field", { field: t("street").toLowerCase() }));
		}
		if (isNullOrEmpty(supplier.address.number)) {
			messages.push(t("fill_in_required_field", { field: t("number").toLowerCase() }));
		}
		if (isNullOrEmpty(supplier.address.postalCode)) {
			messages.push(t("fill_in_required_field", { field: t("postalCode").toLowerCase() }));
		}
		if (isNullOrEmpty(supplier.address.municipality)) {
			messages.push(t("fill_in_required_field", { field: t("municipality").toLowerCase() }));
		}
		if (isNullOrEmpty(supplier.address.country)) {
			messages.push(t("fill_in_required_field", { field: t("country").toLowerCase() }));
		}
		if (isNullOrEmpty(supplier.ourCustomerNumber)) {
			messages.push(t("fill_in_required_field", { field: t("ourCustomerNumber").toLowerCase() }));
		}
		return messages;
	}

	const readonly: boolean = (props as IEditScreenProps<ISupplier>).readonly;

	return (
		<EntityEditor
			width="70%"
			record={supplier}
			endpoint={Endpoint.Suppliers}
			entityState={state}
			dispatch={dispatch}
			dataChanged={dataChange}
			readonly={readonly}
			recordName={supplier.name}
			entityType={t("supplier")}
			close={props.close}
			getErrorMessages={getErrorMessages}
			firstFieldRef={firstField}
		>
			<div className="k-form">
				<label className="k-form-field">
					<span>{t("name")} *</span>
					<Input name="name" ref={firstField} className="full-width-field" value={supplier.name} onChange={onChange} required disabled={readonly} />
				</label>
				<AddressEdit address={supplier.address} addressField="address" onChange={onChange} setAddress={setAddress} required readonly={readonly} />
				<label className="k-form-field">
					<span>{t("ourCustomerNumber")}</span>
					<Input name="ourCustomerNumber" className="full-width-field" value={supplier.ourCustomerNumber} onChange={onChange} required disabled={readonly} />
				</label>
				{!props.hideActive && (
					<div className="k-form-field">
						<span>{t("active")}</span>
						<YesNoSwitch name="active" checked={supplier.active} onChange={onChange} disabled={readonly} />
					</div>
				)}
			</div>
		</EntityEditor>
	);
};

export default SupplierEditor;
